import React, { useState, useEffect, useCallback } from 'react';
import {
    Tabs,
    Card,
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Select,
    Switch,
    Alert,
    message,
    Popconfirm,
    Typography,
    Empty,
} from 'antd';
import {
    FileTextOutlined,
    ArrowLeftOutlined,
    EditOutlined,
    DeleteOutlined,
    CopyOutlined,
    UsergroupAddOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { ENDPOINTS } from '../helpers/api/apiConfig';
import ReportTemplateWizard from './ReportTemplateWizard';
import '../style/Report.css';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface MetricDefinition {
    id: number;
    key: string;
    name: string;
    sourceType: string;
    sourceKey: string;
    unit?: string;
    aggregations?: string[];
    description?: string;
}

interface ReportTemplate {
    id: number;
    key: string;
    name: string;
    description?: string;
    isGlobal: boolean;
    status: string;
    company?: {
        id: number;
        name: string;
    };
    areaTypes?: Array<{
        id: number;
        areaType: {
            id: number;
            name: string;
        };
    }>;
    metrics?: Array<{
        id: number;
        metric: {
            id: number;
            key: string;
            name: string;
        };
    }>;
    patterns?: Array<{
        id: number;
        pattern: {
            id: number;
            name: string;
            patternType: string;
        };
    }>;
    createdBy?: {
        id: number;
        firstName: string;
        lastName: string;
    };
    createdAt: string;
}

interface Company {
    id: number;
    name: string;
}

interface ReportTemplateSettingsProps {
    embedded?: boolean;
}

const ReportTemplateSettings: React.FC<ReportTemplateSettingsProps> = ({ embedded = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const metricRouteBase = embedded ? '/back-office/metrics' : '/metrics';
    const [activeTab, setActiveTab] = useState('browse');
    const [templates, setTemplates] = useState<ReportTemplate[]>([]);
    const [loading, setLoading] = useState(false);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedTemplateForAssign, setSelectedTemplateForAssign] = useState<ReportTemplate | null>(null);
    const [assignForm] = Form.useForm();
    const [metrics, setMetrics] = useState<MetricDefinition[]>([]);
    const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
    const [duplicateTemplateRequest, setDuplicateTemplateRequest] = useState<{ id: number; stamp: number } | null>(null);

    const loadMetrics = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.METRICS, { withCredentials: true });
            setMetrics(res.data?.data || res.data || []);
        } catch (err) {
            message.error('Failed to load metrics');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadTemplates = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.REPORT_TEMPLATES, {
                withCredentials: true
            });
            setTemplates(res.data?.data || res.data || []);
        } catch (err) {
            message.error('Failed to load templates');
        } finally {
            setLoading(false);
        }
    }, []);

    const loadCompanies = useCallback(async () => {
        try {
            const res = await axios.get(ENDPOINTS.GET_COMPANIES, {
                withCredentials: true
            });
            setCompanies(res.data?.data || res.data || []);
        } catch (err) {
            console.error('Failed to load companies');
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'browse') {
            loadTemplates();
        } else if (activeTab === 'metrics') {
            loadMetrics();
        }
        // wizard tab doesn't need to load anything - wizard component handles its own data
    }, [activeTab, loadTemplates, loadMetrics]);

    useEffect(() => {
        const targetTab = (location.state as { tab?: string } | null)?.tab;
        if (targetTab && targetTab !== activeTab) {
            setActiveTab(targetTab);
        }
    }, [location.state, activeTab]);

    useEffect(() => {
        loadCompanies();
    }, [loadCompanies]);

    const handleDelete = async (id: number, name: string) => {
        try {
            await axios.delete(ENDPOINTS.REPORT_TEMPLATE_DETAIL(id), {
                withCredentials: true
            });
            message.success(`Template "${name}" deleted successfully`);
            loadTemplates();
        } catch (err) {
            message.error('Failed to delete template');
        }
    };

    const handleEdit = (template: ReportTemplate) => {
        setDuplicateTemplateRequest(null);
        setEditingTemplateId(template.id);
        setActiveTab('wizard');
    };

    const handleDuplicate = (template: ReportTemplate) => {
        setEditingTemplateId(null);
        setDuplicateTemplateRequest({ id: template.id, stamp: Date.now() });
        setActiveTab('wizard');
    };

    const handleAssignToCompanies = (template: ReportTemplate) => {
        setSelectedTemplateForAssign(template);
        
        // Prepopulate form with current template settings
        assignForm.setFieldsValue({
            isGlobal: template.isGlobal,
            companyId: template.company?.id,
        });
        
        setShowAssignModal(true);
    };

    const handleAssignSubmit = async () => {
        if (!selectedTemplateForAssign) return;
        
        try {
            const values = await assignForm.validateFields();
            
            // Update template with new assignment settings
            const updatePayload: any = {
                isGlobal: values.isGlobal || false,
            };
            
            // If not global, must have a company
            if (!values.isGlobal && !values.companyId) {
                message.error('Please select a company for company-specific templates');
                return;
            }
            
            if (!values.isGlobal && values.companyId) {
                updatePayload.companyId = values.companyId;
            }
            
            console.log('📤 Updating template assignment:', {
                templateId: selectedTemplateForAssign.id,
                payload: updatePayload
            });
            
            const response = await axios.put(
                ENDPOINTS.REPORT_TEMPLATE_DETAIL(selectedTemplateForAssign.id),
                updatePayload,
                { withCredentials: true }
            );
            
            console.log('✅ Template updated:', response.data);
            
            message.success('Template assignment updated successfully');
            setShowAssignModal(false);
            await loadTemplates(); // Reload to show changes
        } catch (err: any) {
            message.error(err.response?.data?.message || 'Failed to update assignment');
        }
    };

    const columns = [
        {
            title: 'Template Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: ReportTemplate) => (
                <Space direction="vertical" size="small">
                    <Text strong>{text}</Text>
                    {record.description && (
                        <Text type="secondary" style={{ fontSize: 12 }}>{record.description}</Text>
                    )}
                </Space>
            ),
        },
        {
            title: 'Linked Entities',
            key: 'linkedEntities',
            render: (_: any, record: ReportTemplate) => {
                const hasAreas = record.areaTypes && record.areaTypes.length > 0;
                const hasMetrics = record.metrics && record.metrics.length > 0;
                const hasPatterns = record.patterns && record.patterns.length > 0;
                
                if (!hasAreas && !hasMetrics && !hasPatterns) {
                    return <Tag color="purple">Universal</Tag>;
                }
                
                return (
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        {hasAreas && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>Areas: </Text>
                                <Space wrap size="small">
                                    {record.areaTypes!.map((at) => (
                                        <Tag key={at.id} style={{ fontSize: 11 }}>{at.areaType?.name}</Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                        {hasMetrics && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>Metrics: </Text>
                                <Space wrap size="small">
                                    {record.metrics!.map((m) => (
                                        <Tag key={m.id} color="green" style={{ fontSize: 11 }}>{m.metric?.key}</Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                        {hasPatterns && (
                            <div>
                                <Text type="secondary" style={{ fontSize: 11 }}>Patterns: </Text>
                                <Space wrap size="small">
                                    {record.patterns!.map((p) => (
                                        <Tag key={p.id} color="orange" style={{ fontSize: 11 }}>{p.pattern?.name}</Tag>
                                    ))}
                                </Space>
                            </div>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Scope',
            key: 'scope',
            render: (_: any, record: ReportTemplate) => (
                record.isGlobal ? (
                    <Tag color="blue">Global</Tag>
                ) : (
                    <Tag>{record.company?.name || 'Company'}</Tag>
                )
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'active' ? 'green' : 'default'}>
                    {status.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: ReportTemplate) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleDuplicate(record)}
                    >
                        Duplicate
                    </Button>
                    <Button
                        size="small"
                        icon={<UsergroupAddOutlined />}
                        onClick={() => handleAssignToCompanies(record)}
                    >
                        Assign
                    </Button>
                    <Popconfirm
                        title="Delete this template?"
                        description="This will permanently delete the template."
                        onConfirm={() => handleDelete(record.id, record.name)}
                        okText="Delete"
                        cancelText="Cancel"
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                        >
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className={`report-settings-page${embedded ? ' embedded' : ''}`}>
            {!embedded && (
                <>
                    <Space direction="vertical" size={0} className="report-settings-header">
                        <Button
                            type="link"
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/configuration')}
                            style={{ paddingLeft: 0 }}
                        >
                            Back to Settings
                        </Button>
                        <Title level={2} style={{ margin: 0 }}>Report Template Settings</Title>
                    </Space>
                    <Text type="secondary" className="report-settings-subtitle">
                        Manage report templates and assignments (Master users only)
                    </Text>
                </>
            )}

            <Card className={`report-settings-card${embedded ? ' embedded' : ''}`}>
                <Tabs activeKey={activeTab} onChange={setActiveTab}>
                    <TabPane tab="Browse Templates" key="browse">
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setActiveTab('wizard')}
                            >
                                Create New Template
                            </Button>

                            <Table
                                loading={loading}
                                dataSource={templates}
                                columns={columns}
                                rowKey="id"
                                pagination={{ pageSize: 10 }}
                                locale={{ emptyText: <Empty description="No templates found" /> }}
                            />
                        </Space>
                    </TabPane>

                    <TabPane tab="Create Template" key="wizard">
                        <ReportTemplateWizard 
                            editingTemplateId={editingTemplateId ?? undefined}
                            duplicateTemplateRequest={duplicateTemplateRequest ?? undefined}
                            onSaved={() => {
                                setEditingTemplateId(null);
                                setDuplicateTemplateRequest(null);
                                setActiveTab('browse');
                                loadTemplates();
                            }}
                        />
                    </TabPane>

                    <TabPane tab="Metrics" key="metrics">
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate(`${metricRouteBase}/new`)}
                            >
                                Create New Metric
                            </Button>

                            <Table
                                loading={loading}
                                dataSource={metrics}
                                columns={[
                                    {
                                        title: 'Key',
                                        dataIndex: 'key',
                                        key: 'key',
                                        render: (text: string) => <Tag color="blue">{text}</Tag>,
                                    },
                                    {
                                        title: 'Name',
                                        dataIndex: 'name',
                                        key: 'name',
                                    },
                                    {
                                        title: 'Source',
                                        key: 'source',
                                        render: (_: any, record: MetricDefinition) => (
                                            <Text type="secondary">
                                                {record.sourceType}: {record.sourceKey}
                                            </Text>
                                        ),
                                    },
                                    {
                                        title: 'Unit',
                                        dataIndex: 'unit',
                                        key: 'unit',
                                        render: (unit: string) => unit || '—',
                                    },
                                    {
                                        title: 'Aggregations',
                                        dataIndex: 'aggregations',
                                        key: 'aggregations',
                                        render: (aggs: string[]) => (
                                            <Space wrap>
                                                {aggs?.map(agg => (
                                                    <Tag key={agg} style={{ fontSize: 11 }}>{agg}</Tag>
                                                ))}
                                            </Space>
                                        ),
                                    },
                                    {
                                        title: 'Actions',
                                        key: 'actions',
                                        render: (_: any, record: MetricDefinition) => (
                                            <Space>
                                                <Button
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={() => navigate(`${metricRouteBase}/${record.id}/edit`)}
                                                >
                                                    Edit
                                                </Button>
                                                <Popconfirm
                                                    title="Delete this metric?"
                                                    description="This action cannot be undone."
                                                    onConfirm={async () => {
                                                        try {
                                                            await axios.delete(ENDPOINTS.METRIC_DETAIL(record.id), {
                                                                withCredentials: true
                                                            });
                                                            message.success(`Metric "${record.name}" deleted successfully`);
                                                            loadMetrics();
                                                        } catch (err) {
                                                            message.error('Failed to delete metric');
                                                        }
                                                    }}
                                                    okText="Delete"
                                                    cancelText="Cancel"
                                                >
                                                    <Button
                                                        size="small"
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Popconfirm>
                                            </Space>
                                        ),
                                    },
                                ]}
                                rowKey="id"
                                pagination={{ pageSize: 10 }}
                                locale={{ emptyText: <Empty description="No metrics found" /> }}
                            />
                        </Space>
                    </TabPane>
                </Tabs>
            </Card>

            {/* Company Assignment Modal */}
            <Modal
                title={`Edit Assignment: ${selectedTemplateForAssign?.name}`}
                open={showAssignModal}
                onOk={handleAssignSubmit}
                onCancel={() => {
                    setShowAssignModal(false);
                    assignForm.resetFields();
                }}
                width={600}
            >
                <Form form={assignForm} layout="vertical">
                    <Form.Item
                        label="Global Template"
                        name="isGlobal"
                        valuePropName="checked"
                        help="Global templates are visible to all companies"
                    >
                        <Switch checkedChildren="Global" unCheckedChildren="Company" />
                    </Form.Item>
                    
                    {/* Company selector - conditional on isGlobal */}
                    <Form.Item noStyle shouldUpdate={(prev, curr) => prev.isGlobal !== curr.isGlobal}>
                        {({ getFieldValue }) => {
                            const isGlobal = getFieldValue('isGlobal');
                            
                            if (isGlobal) {
                                return (
                                    <Alert
                                        message="Global Template"
                                        description="This template will be visible to ALL companies system-wide."
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />
                                );
                            }
                            
                            return (
                                <Form.Item
                                    label="Assign to Company"
                                    name="companyId"
                                    rules={[{ required: true, message: 'Please select a company' }]}
                                    help="Select which company will have access to this template"
                                >
                                    <Select
                                        placeholder="Select company"
                                        options={companies.map(c => ({ label: c.name, value: c.id }))}
                                        showSearch
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </Form>
            </Modal>

        </div>
    );
};

export default ReportTemplateSettings;
